import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { array, arrayOf, bool, func, number, object, oneOfType, shape, string } from 'prop-types';
import { showResetBtn } from '../../../lib/rulesBasedPricingHelpers';
import FacetPanel from 'ps-components/FacetPanel/FacetPanel';
import SurveyJobRow from '../../JobMatching/ResultDetails/SurveyJobRow';
import JobMatchingHeader from './JobMatchingHeader/JobMatchingHeader';
import SubHeaderSearch from '../../JobMatching/ResultDetails/SubHeaderSearch';
import SurveyJobMatchingList from './SurveyJobMatchingList/SurveyJobMatchingList';
import { updateJobMatches } from '../../JobMatching/OrgJobsActions';
import { surveyFacetSearchActions } from '../../../reducers/modules/surveys/surveyFacetSearch/surveyFacetSearch';
import { areAllFacetsChecked, getQueryParams } from '../../../lib/facetHelpers';
import { setSurveyJobFacetText, toggleShowSelectedSurveyJobs } from '../../JobMatching/SurveyJobsActions';
import { hideModal } from '../../../actions/modal';
import './MatchingModal.scss';

const ENTER_KEY = 13;

class MatchingModal extends Component {
  static propTypes = {
    actions: shape({
      facetItemClick: func,
      facetReset: func,
      hideModal: func,
      pageList: func,
      search: func,
      setSurveyJobFacetText: func,
      toggleShowSelectedSurveyJobs: func,
      updateJobMatches: func
    }),
    facetSelection: shape({
      pub_survey_publisher: object,
      survey_code: object,
      survey_year: object
    }),
    facets: arrayOf(
      shape({
        field: string,
        id: string,
        items: arrayOf(
          shape({
            doc_count: number,
            key: oneOfType([string, number])
          })
        ),
        title: string,
        type: string
      })
    ),
    items: arrayOf(
      shape({
        code: string,
        description: string,
        publisher: string,
        survey: shape({
          code: string,
          name: string
        }),
        title: string,
        years: number
      })
    ),
    lastQueryParams: shape({
      es_query: string,
      exclude: object,
      include: object,
      limit: number,
      offset: number,
      pageName: string
    }),
    loaded: bool,
    loading: bool,
    modal: shape({
      modalProps: shape({
        className: string,
        orgJob: shape({
          code: string,
          codeKey: string,
          currency: string,
          description: string,
          descriptionDocument: shape({
            fileLocation: string,
            title: string
          }),
          employeeCount: oneOfType([string, number]),
          id: oneOfType([string, number]),
          jobMatches: array,
          name: string
        })
      }),
      modalType: string
    }),
    modalProps: shape({
      className: string,
      orgJob: shape({
        code: string,
        codeKey: string,
        currency: string,
        description: string,
        descriptionDocument: shape({
          fileLocation: string,
          title: string
        }),
        employeeCount: oneOfType([string, number]),
        id: oneOfType([string, number]),
        jobMatches: array,
        name: string
      }),
      modalType: string
    }),
    modalType: string,
    offset: number,
    replacementTokens: arrayOf(
      shape({
        token: string,
        value: string
      })
    ),
    searchResultsCount: number,
    selectedJob: shape({
      code: string,
      codeKey: string,
      currency: string,
      description: string,
      descriptionDocument: shape({
        fileLocation: string,
        title: string
      }),
      employeeCount: oneOfType([string, number]),
      id: oneOfType([string, number]),
      jobMatches: array,
      name: string
    }),
    showZeros: bool,
    surveyJobs: shape({
      isSelected: bool,
      propMatches: shape({
        loading: bool,
        propagatedMatches: object
      }),
      searchText: string,
      selectedSurveyJobCodes: array,
      surveyJobs: array
    }),
    useJobCodeKey: bool
  };

  state = {
    showJobDescriptions: false,
    tempMatchSelections: []
  };

  componentDidMount() {
    const { actions, selectedJob } = this.props;
    this.handleReset();

    const currentMatches =
      selectedJob && selectedJob.jobMatches
        ? selectedJob.jobMatches.map(connection => {
            return {
              surveyJobCode: connection.surveyJob.code,
              surveyCode: connection.surveyJob.survey.code,
              surveyJobId: connection.surveyJob.id
            };
          })
        : [];

    this.setState({ tempMatchSelections: currentMatches });
  }

  componentDidUpdate(prevProps) {
    const { modal, selectedJob } = this.props;
    if (modal.open !== prevProps.modal.open || selectedJob.currency !== prevProps.selectedJob.currency) {
      this.handleReset();
    }
  }

  componentWillUnmount() {
    this.handleReset();
  }

  clearSearch = () => {
    const { actions } = this.props;

    actions.searchInputSearch('');
    actions.setSurveyJobFacetText('');
  };

  handleReset = () => {
    const { actions, name } = this.props;

    actions.search({
      params: getQueryParams(name),
      clear: true
    });

    actions.setSurveyJobFacetText('');
  };

  pageSurveyJobs = (first, newAfter) => {
    const { actions } = this.props;

    actions.pageList(first, newAfter);
  };

  searchByText = e => {
    const { actions, name, surveyJobs } = this.props;
    if (e.keyCode === ENTER_KEY && surveyJobs.isSelected) {
      actions.search({
        params: getQueryParams(name, surveyJobs.searchText),
        clear: false
      });
    }
  };

  toggleJobDescriptions = () => {
    const { showJobDescriptions } = this.state;
    this.setState({ showJobDescriptions: !showJobDescriptions });
  };

  updateTempMatches = (code, surveyCode, surveyJobId) => {
    const { tempMatchSelections } = this.state;
    const { actions, selectedJob } = this.props;
    const newMatches = tempMatchSelections.find(match => match.surveyJobId === surveyJobId)
      ? tempMatchSelections.filter(job => job.surveyJobId !== surveyJobId)
      : [...tempMatchSelections, { surveyJobCode: code, surveyCode: surveyCode, surveyJobId: surveyJobId }];

    this.setState({ tempMatchSelections: newMatches });
  };

  render() {
    const { showJobDescriptions, tempMatchSelections } = this.state;
    const {
      actions,
      facets,
      facetSelection,
      history,
      items,
      lastFacetFilteredID,
      lastQueryParams,
      loaded,
      loading,
      replacementTokens,
      searchResultsCount,
      selectedJob,
      showZeros,
      surveyJobs,
      useJobCodeKey
    } = this.props;
    const selectedJobConnection = selectedJob && selectedJob.jobMatches ? selectedJob.jobMatches : [];
    const jobsToRender =
      surveyJobs.isSelected && surveyJobs.surveyJobs.length
        ? surveyJobs.surveyJobs
        : !surveyJobs.isSelected
        ? selectedJobConnection.map(job => job.surveyJob)
        : [];
    const showResetButton =
      areAllFacetsChecked(facets, facetSelection) || (lastQueryParams && lastQueryParams.es_query !== '');

    return (
      <div className="survey__jobs">
        <JobMatchingHeader
          hideModal={actions.hideModal}
          history={history}
          replacementTokens={replacementTokens}
          selectedJob={selectedJob}
          showJobDescriptions={showJobDescriptions}
          tempMatchSelections={tempMatchSelections}
          updateJobMatches={actions.updateJobMatches}
          useJobCodeKey={useJobCodeKey}
        />
        <div className="body">
          <FacetPanel
            className="survey__job-facets"
            facetItemClick={actions.facetItemClick}
            facetReset={actions.facetReset}
            facets={facets}
            facetSelection={facetSelection}
            isDisabled={!surveyJobs.isSelected}
            isLoaded={loaded}
            lastFacetFilteredID={lastFacetFilteredID}
            limit={5}
            resetSearch={this.handleReset}
            showFacetSectionSearch={true}
            showResetButton={showResetButton}
            showZeros={showZeros}
          />
          <div className="survey__job-results">
            <SubHeaderSearch
              clearSearch={this.clearSearch}
              isSelected={surveyJobs.isSelected}
              placeholderText="Search survey jobs"
              searchId="survey__jobs-search-global-job-matching"
              searchByText={this.searchByText}
              searchText={surveyJobs.searchText}
              selectedItems={selectedJobConnection}
              setSearchText={actions.setSurveyJobFacetText}
              toggleSelected={actions.toggleShowSelectedSurveyJobs}
              totalCount={searchResultsCount}
            />
            <SurveyJobMatchingList
              isSelected={surveyJobs.isSelected}
              jobs={jobsToRender}
              loaded={loaded}
              loading={loading}
              pageAction={this.pageSurveyJobs}
              selectedJob={selectedJob}
              selectedJobConnection={selectedJobConnection}
              showJobDescriptions={showJobDescriptions}
              surveyJobs={items}
              totalCount={searchResultsCount}
              toggleJobDescriptions={this.toggleJobDescriptions}
              updateTempMatches={this.updateTempMatches}
              tempMatchSelections={tempMatchSelections}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ orgJobs, surveyJobs, session, surveyFacetSearch }) => ({
  selectedJob: orgJobs.selectedJob,
  surveyJobs,
  replacementTokens: session.replacementTokens,
  useJobCodeKey: session.organization.useJobCodeKey,
  facets: surveyFacetSearch.facets,
  facetSelection: surveyFacetSearch.facetSelection,
  items: surveyFacetSearch.items,
  lastFacetFilteredID: surveyFacetSearch.lastFacetFilteredID,
  lastQueryParams: surveyFacetSearch.lastQueryParams,
  loaded: surveyFacetSearch.loaded,
  loading: surveyFacetSearch.loading,
  name: surveyFacetSearch.name,
  offset: surveyFacetSearch.offset,
  searchResultsCount: surveyFacetSearch.searchResultsCount,
  showZeros: surveyFacetSearch.showZeros
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      facetItemClick: surveyFacetSearchActions.facetItemClick,
      facetReset: surveyFacetSearchActions.facetReset,
      hideModal,
      pageList: surveyFacetSearchActions.pageList,
      search: surveyFacetSearchActions.search,
      searchInputSearch: surveyFacetSearchActions.searchInputSearch,
      setSurveyJobFacetText,
      toggleShowSelectedSurveyJobs,
      updateJobMatches
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(MatchingModal);
